import { useEffect, useState } from "react";
import {
  getStatistics,
  createStatistic,
  updateStatistic,
  deleteStatistic,
} from "../../../api/apiServices";

export default function StatisticsSection() {
  const [stats, setStats] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await getStatistics();
      setStats(res.data || []);
    } catch (err) {
      console.error("Failed to fetch statistics", err);
    }
  };

  /* ---------------- UPDATE LOCAL ---------------- */
  const updateStat = (id, field, value) => {
    setStats((prev) =>
      prev.map((s) =>
        s?._id === id ? { ...s, [field]: value } : s
      )
    );
  };

  /* ---------------- ADD ---------------- */
  const addStat = async () => {
    try {
      const res = await createStatistic({
        number: "0",
        label: "New Statistic",
      });

      setStats((prev) => [...prev, res]);
    } catch (err) {
      console.error("Add stat failed", err);
    }
  };

  /* ---------------- DELETE ---------------- */
  const removeStat = async (id) => {
    if (stats.length <= 1) return;

    try {
      await deleteStatistic(id);
      setStats((prev) => prev.filter((s) => s._id !== id));
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* ---------------- SAVE ALL ---------------- */
  const handleSave = async () => {
    setIsSaving(true);

    try {
      await Promise.all(
        stats.map((stat) =>
          updateStatistic(stat._id, {
            number: stat.number,
            label: stat.label,
          })
        )
      );

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Statistics
          </h2>
          <p className="text-gray-600 text-sm">
            Manage your impact numbers
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={addStat}
            className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <i className="ri-add-line"></i>
            Add Stat
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#5a3ba3] disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-check-line"></i>
          Statistics saved successfully!
        </div>
      )}

      {/* PREVIEW */}
      <div className="bg-[#6a4bc3] rounded-xl p-8">
        <h3 className="text-white/80 text-sm mb-6">Preview</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat?._id} className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-[#c9a24d] mb-2">
                {stat?.number}
              </div>
              <div className="text-white text-sm">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* EDIT */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">
          Edit Statistics
        </h3>

        <div className="space-y-4">
          {stats.map((stat, index) => (
            <div
              key={stat?._id}
              className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
            >
              <span className="text-sm text-gray-500 w-8">
                {index + 1}.
              </span>

              <div className="flex-1 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Number / Value
                  </label>
                  <input
                    type="text"
                    value={stat?.number}
                    onChange={(e) =>
                      updateStat(stat?._id, "number", e.target.value)
                    }
                    className="w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#6a4bc3]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Label
                  </label>
                  <input
                    type="text"
                    value={stat?.label}
                    onChange={(e) =>
                      updateStat(stat?._id, "label", e.target.value)
                    }
                    className="w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#6a4bc3]"
                  />
                </div>
              </div>

              <button
                onClick={() => removeStat(stat._id)}
                disabled={stats?.length <= 1}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
